import { signInWithCustomToken, signOut } from 'firebase/auth';
import { doc, serverTimestamp, updateDoc } from 'firebase/firestore';
import {
    commandPromptIds,
    complianceRoles,
    globalCommandOptions,
    PAGE_KEYS,
    popupSpecs, popupSpecsForDes, popupSpecsForDownloadOrders,
    popupSpecsForQuickPrice,
    popupSpecsForTrade
} from 'lib/constants';
import { openTrackedWindow } from 'services/window-geometry.service';
import TooltipWithArrow from 'molecules/TooltipWithArrow';
import { nanoid } from 'nanoid';
import Image from 'next/image';
import { useRouter } from 'next/router';
import GlobalSearch from 'organisms/GlobalSearch';
import { useContext, useEffect, useMemo, useState } from 'react';
import { getAuth, logout, setAuth } from 'services/auth.service';
import { getMediaFromCloudFrontUrl } from 'services/aws.service';
import {
    db,
    auth as firebaseAuth,
    updateDocs
} from 'services/firebase.service';
import { socket, socketEmit } from 'services/socket';
import { StatusContext } from '@/components/context/StatusContext';
import { DataContext } from '@/components/context/DataContext';
import { Search } from 'react-feather';
import HelpButton from 'organisms/HelpButton';
import useIrsBrokerType from 'hooks/useIrsBrokerType';
import MenuList from '../atoms/MenuList';
import Text from '../atoms/Text';
import ActionList from '../molecules/ActionList';
import { trackEvent } from '@/helper/mixPanel';
import SubFundSidebar from './SubFundSidebar';
import { getUserPreference } from '@/services/user-preference.service';
import PartialFillCard from './PartialFillCard';
import MatchSuccessfulDialog from './MatchSuccessfulDialog';
import BrokerPriceHitDialog from './Clob/BrokerPriceHitDialog';
import { getBrokerSelfPriceNotifications, dismissBrokerSelfPriceNotification } from '@/services/broker-price-hit.service';
import ChildWindowsSidebar from './ChildWindowsSidebar';

const menus = {
    superMenu: [
        {
            id: 1,
            title: 'Dashboard',
            isActive: false,
            link: '/dashboard'
        },
        {
            id: 2,
            title: 'Trade',
            isActive: false,
            link: '/clob'
        },
        {
            id: 3,
            title: 'Users',
            isActive: false,
            link: '/users'
        },
        {
            id: 4,
            title: 'Sectors',
            isActive: false,
            link: '/sectors'
        },
        {
            id: 5,
            title: 'Companies',
            isActive: false,
            link: '/companies'
        },
        {
            id: 6,
            title: 'Bonds',
            isActive: false,
            link: '/bonds'
        },
        {
            id: 7,
            title: 'Umbrella Orgs',
            isActive: false,
            link: '/umbrella-org'
        },
        {
            id: 8,
            title: 'LEs',
            isActive: false,
            link: '/clients'
        }
    ],
    userMenu: [
        {
            id: 1,
            title: 'Dashboard',
            isActive: false,
            link: '/dashboard'
        },
        {
            id: 2,
            title: 'Trade',
            isActive: false,
            link: '/clob'
        }
    ]
};

const Header = () => {
    const [menuItem, setMenu] = useState([]);
    const [showSubFundSidebar, setShowSubFundSidebar] = useState(false);
    const [user, setUser] = useState({});
    const router = useRouter();
    const { setAuto } = useContext(StatusContext);
    const [subFunds, setSubFunds] = useState([]);
    const { setUserPreferences } = useContext(DataContext);
    const { brokerType: irsBrokerType } = useIrsBrokerType(
        'irsDashboardDefaultTab',
    );

    const [confirms, setConfirms] = useState([]);
    const [selfHits, setSelfHits] = useState([]);
    const [isChildWindowsOpen, setIsChildWindowsOpen] = useState(false);

    // Broker Price Hit notifications for an IDB broker: seeded on mount (covers
    // refresh / navigation / re-login / offline-at-fill) and refetched when the
    // broker's own SELF price streams a fill on the CLOB — no new socket, no poll.
    const fetchSelfHits = async () => {
        if (getAuth()?.role !== 'Broker' || irsBrokerType !== 'IDB') return;
        const response = await getBrokerSelfPriceNotifications();
        if (response?.status) setSelfHits(response?.data?.entity ?? []);
    };
    const dismissSelfHit = async (hit) => {
        setSelfHits((prev) => prev.filter((h) => h.id !== hit.id));
        await dismissBrokerSelfPriceNotification(hit.id);
    };
    const onOwnPriceUpdate = (payload) => {
        const level = payload?.level;
        if (level?.isSelf && level.userId === getAuth()?.id) fetchSelfHits();
    };
    const onPartialMatch = (m) => {
        const score = {
            ACTION: 0,
            PARTIAL: 1,
            SUCCESS: 2
        };
        if (m.status === 'CLOSED') setConfirms((p) => p.filter((i) => i.id !== m.id));
        else {
            const incoming = Array.isArray(m) ? m : [m];
            const incomingIds = new Set(incoming.map((i) => i.id));
            setConfirms((p) => [...incoming, ...p.filter((i) => !incomingIds.has(i.id))]
                .sort((a, b) => score[b.type] - score[a.type]));
        }
    };

    const fetchUserPreference = async () => {
        const response = await getUserPreference();

        if (response?.status) {
            setUserPreferences(response?.data?.entity);
        }
    };

    const logoutFn = async () => {
        const auth = getAuth();
        socketEmit('/post/users/sessions/logout', { userId: auth?.id });
        if (socket.connected) socket.disconnect();
        try {
            await signOut(firebaseAuth);
        } catch (e) {
            console.log(e);
        }
        logout();
    };

    const onSessionCreated = (data) => {
        setAuto(data.auto);
        if (data.isDeactivatedUser || data.forceLogout) logoutFn();
    };

    const fireBaseSync = async (isChat, fbToken) => {
        if (isChat) {
            try {
                await signInWithCustomToken(firebaseAuth, fbToken);
            } catch (loginError) {
                console.error('Login error:', loginError);
            }
        } else {
            const auth = getAuth();
            try {
                const userDocRef = doc(db, 'presence', auth?.id);
                await updateDocs(userDocRef, { online: false });
                await signOut(firebaseAuth);
            } catch (e) {
                console.log(e);
            }
        }
    };

    const onUpdateChatHoot = (data) => {
        const auth = getAuth();
        if (
            auth?.isChatEnabled === data?.isChatEnabled
            && auth?.isHootEnabled === data?.isHootEnabled
        ) {
            return;
        }
        setAuth(
            JSON.stringify({
                ...auth,
                isChatEnabled: data?.isChatEnabled,
                isHootEnabled: data?.isHootEnabled
            }),
        );
        if (auth?.isChatEnabled !== data?.isChatEnabled) {
            fireBaseSync(data?.isChatEnabled, data?.fbToken);
        }
    };

    const getLatestCurrencyPair = ({ data, error, isFxoButton }) => {
        if (error || !isFxoButton) return;
        const currencyPair = data[0];

        if (currencyPair?.id && currencyPair?.ticker) {
            const sessionData = {
                currencyPair,
                origin: 'fxo_button'
            };
            sessionStorage.setItem('fxoInput', JSON.stringify(sessionData));
            window.open(
                `${process.env.NEXT_PUBLIC_UI_URL}fx-options/form`,
                'fxo_window',
                popupSpecsForDes,
            );
            const pathArray = router.asPath.split('?')[0].split('/');
            if (pathArray[1] !== 'clob') {
                router.push('/clob');
            }
        }
    };

    const onGetIrsCurves = ({ data, error, isIrsButton }) => {
        if (error || !isIrsButton) return;
        const irsCurve = data[0];

        if (irsCurve?.id) {
            const sessionData = { irsCurve };
            sessionStorage.setItem('irsInput', JSON.stringify(sessionData));
            window.open(
                `${process.env.NEXT_PUBLIC_UI_URL}irs/form`,
                'irs_form',
                popupSpecsForDes,
            );
            const pathArray = router.asPath.split('?')[0].split('/');
            if (pathArray[1] !== 'clob') {
                router.push('/clob');
            }
        }
    };
    const updateUserlastSeen = async (id) => {
        if (!id) return;

        const userDocRef = doc(db, 'users', id);
        await updateDoc(userDocRef, { lastSeen: serverTimestamp() });
    };

    const onUserSubFundSplits = (response) => {
        if (response?.data) {
            setSubFunds(response.data);
        } else {
            setSubFunds([]);
        }
    };

    const handleFundsUpdated = (updatedFunds) => {
        setSubFunds(updatedFunds);
    };

    const HeaderPaths = [
        '/my-orders',
        '/dashboard',
        '/recent',
        '/sectors/interest',
        '/bonds',
        '/users',
        '/sectors',
        '/companies',
        '/compliance/users',
        '/umbrella-org',
        '/clients',
        '/matchings/irs',
        '/clob'
    ];
    const partialTrade = ({ id }) => {
        socketEmit('/put/irs-matching-popups', { id });
    };
    const specialPath = '/atlas';
    useEffect(() => {
        socket.on('/post/users/sessions/:sessionId', onSessionCreated);
        socket.on('/user-status', onSessionCreated);
        socket.on('/update-chat-hoot', onUpdateChatHoot);
        socket.on('/fx-options/currency-pairs', getLatestCurrencyPair);
        socket.on('/irs/user-irs-activity', onGetIrsCurves);
        socket.on('/get/user-sub-fund-splits', onUserSubFundSplits);

        if ([...HeaderPaths, specialPath].includes(router.pathname)) {
            socketEmit('/get/irs-matching-popups');
            socket.on('/get/irs-matching-popups', onPartialMatch);
            socket.on('/irs-matching-partial-trade', partialTrade);
            fetchSelfHits();
            socket.on('/new-irs-price', onOwnPriceUpdate);
        }

        if (router.pathname) {
            const data = getAuth();
            updateUserlastSeen(data?.id);
            fetchUserPreference();
            if (data) {
                socketEmit('/get/user-sub-fund-splits', { userId: data.id });
                let path = router?.pathname || '';
                path = path.replace(/\[.*?\]/g, router?.query?.id || '');
                trackEvent('User Active', {
                    Target: path,
                    company: data?.companyName,
                    role: data?.role
                });
            }
            setUser(data);
            setMenu(
                (data?.role === 'SuperAdmin' ? menus.superMenu : menus.userMenu).map(
                    (item) => {
                        if (router.pathname === item.link) return { ...item, isActive: true };
                        return { ...item, isActive: false };
                    },
                ),
            );
        }
        return () => {
            if ([...HeaderPaths, specialPath].includes(router.pathname)) {
                socket.off('/get/irs-matching-popups', onPartialMatch);
                socket.off('/irs-matching-partial-trade', partialTrade);
                socket.off('/new-irs-price', onOwnPriceUpdate);
            }
            socket.off('/post/users/sessions/:sessionId', onSessionCreated);
            socket.off('/user-status', onSessionCreated);
            socket.off('/update-chat-hoot', onUpdateChatHoot);
            socket.off('/fx-options/currency-pairs', getLatestCurrencyPair);
            socket.off('/irs/user-irs-activity', onGetIrsCurves);
        };
    }, [router.pathname]);

    if (!user?.token) return null;
    if (router.pathname === specialPath) {
        return <>
            {confirms.map((m) => (
                m.type === 'SUCCESS'
                    ? <MatchSuccessfulDialog
                        data={m.metadata} key={m.id}
                        endTime={new Date(m.endsAt)}
                        totalTime={(new Date(m.endsAt).getTime() - new Date(m.createdAt).getTime()) / 1000}
                        close={() => {
                            socketEmit('/put/irs-matching-popups', { id: m.id });
                        }}
                    />
                    : m.metadata && <PartialFillCard
                        isPartial={m.metadata.ip}
                        metadata={m.metadata}
                        endTime={new Date(m.endsAt)}
                        key={m.id}
                        close={() => {
                            setConfirms((p) => p.filter((f) => f.id !== m.id));
                        }}
                        pid={m.id}
                        totalTime={(new Date(m.endsAt).getTime() - new Date(m.createdAt).getTime()) / 1000}
                    />
            ))}
            <BrokerPriceHitDialog hits={selfHits} onDismiss={dismissSelfHit} />
        </>;
    }
    if (!HeaderPaths.includes(router.pathname)) return null;

    const openChatWindow = () => {
        const url = '/chat';
        const windowName = 'chat';
        window.open(url, windowName, popupSpecs);
    };

    return (
        <>
            {confirms.map((m) => (
                m.type === 'SUCCESS'
                    ? <MatchSuccessfulDialog
                        data={m.metadata}
                        key={m.id}
                        endTime={new Date(m.endsAt)}
                        totalTime={(new Date(m.endsAt).getTime() - new Date(m.createdAt).getTime()) / 1000}
                        close={() => {
                            socketEmit('/put/irs-matching-popups', { id: m.id });
                        }} />
                    : (m.metadata) && <PartialFillCard
                        isPartial={m.metadata.ip}
                        metadata={m.metadata}
                        endTime={new Date(m.endsAt)}
                        key={m.id}
                        close={() => {
                            setConfirms((p) => p.filter((f) => f.id !== m.id));
                        }}
                        pid={m.id}
                        totalTime={(new Date(m.endsAt).getTime() - new Date(m.createdAt).getTime()) / 1000}
                    />
            ))}
            <BrokerPriceHitDialog hits={selfHits} onDismiss={dismissSelfHit} />
            <div className="sticky top-0 z-[999] bg-basic-200">
                <div className="pl-6 pr-[74px] flex justify-between items-center h-16">
                    <div className="2xl:w-2/12">
                        {router.pathname === '/clob' ? (
                            <div className='h-[40px] w-[250px] relative'>
                                <Image
                                    src="/images/Powered-By-Deon.svg"
                                    alt="logo"
                                    layout="fill"
                                />
                            </div>
                        ) : (
                            <div className='h-[40px] w-[56px] relative'>
                                <Image
                                    src="/images/deon-logo-svg.svg"
                                    alt="logo"
                                    layout="fill"
                                />
                            </div>
                        )}
                    </div>

                    {!complianceRoles.includes(getAuth()?.role) && (
                        <div className="w-9/12 flex items-center justify-center">
                            <div className="mr-1 w-80 lg:w-96 xl:min-w-[380px] 2xl:min-w-128">
                                <GlobalSearch
                                    inputClass="h-9"
                                    globalSearchList="w-80 lg:w-128 xl:w-128"
                                    isCommandLine={false}
                                    voiceCommand={['/clob'].includes(router.pathname)}
                                    icon={<Search size={20} />}
                                />
                            </div>
                            <MenuList
                                menuItem={menuItem}
                                className="text-gray-100 break-keep"
                                listClass="xl:flex hidden"
                            />
                        </div>
                    )}

                    <div className="w-1/12">
                        <div className="flex items-center justify-end gap-2 lg:gap-6">
                            {!complianceRoles.includes(getAuth()?.role) && (
                                <div className="flex gap-5 items-center w-12 flex-col absolute gap-1 bg-primary-400 top-0 right-0 pt-3 px-1 z-50 h-[100vh] border-l border-gray-500">
                                    {getAuth()?.role !== 'SuperAdmin' && (
                                        <TooltipWithArrow
                                            tooltipText="Trade Panel"
                                            childNode={
                                                <Image
                                                    src="/images/icons/hard-drive.svg"
                                                    id="deon-trade-panel"
                                                    width={25}
                                                    height={25}
                                                    alt="trade-panel"
                                                    className="cursor-pointer"
                                                    onClick={() => window.open('/trades', 'trade', popupSpecsForTrade)
                                                    }
                                                />
                                            }
                                        />
                                    )}
                                    <TooltipWithArrow
                                        tooltipText="D.A.N.T.E"
                                        childNode={
                                            <Image src="/images/icons/chatbot.svg"
                                                id='chatbot'
                                                width={27}
                                                height={27}
                                                alt='dante'
                                                className='cursor-pointer'
                                                onClick={() => window.open('/flow', 'dante', popupSpecsForTrade)} />
                                        }
                                    />
                                    {getAuth()?.isHootEnabled && (
                                        <TooltipWithArrow
                                            tooltipText="Hoot"
                                            childNode={
                                                <Image
                                                    src="/images/icons/speaker.svg"
                                                    id="deon-hoot"
                                                    width={25}
                                                    height={25}
                                                    alt="Hoot"
                                                    className="cursor-pointer"
                                                    onClick={() => {
                                                        const windowFeatures = 'popup=true,left=0,top=0,right=0,bottom=0,width=528,height=2000';
                                                        window.open(
                                                            `${process.env.NEXT_PUBLIC_UI_URL}hoot`,
                                                            'hoot',
                                                            windowFeatures,
                                                        );
                                                    }}
                                                />
                                            }
                                        />
                                    )}
                                    {getAuth()?.isChatEnabled && (
                                        <TooltipWithArrow
                                            tooltipText="Chat"
                                            childNode={
                                                <Image
                                                    src="/images/icons/message-square.svg"
                                                    id="deon-chat"
                                                    width={25}
                                                    height={25}
                                                    alt="deon-chat"
                                                    className="cursor-pointer"
                                                    onClick={() => openChatWindow()}
                                                />
                                            }
                                        />
                                    )}
                                    <TooltipWithArrow
                                        tooltipText="Command Prompt"
                                        childNode={
                                            <Image
                                                src="/images/icons/cmd-prompt.svg"
                                                id="deon-cmd-prompt"
                                                width={25}
                                                height={25}
                                                alt="cmd-prompt"
                                                className="cursor-pointer"
                                                onClick={() => {
                                                    window.open(
                                                        `${process.env.NEXT_PUBLIC_UI_URL}irs/${commandPromptIds[
                                                        process.env.NEXT_PUBLIC_ENVIRONMENT
                                                        || 'production'
                                                        ]
                                                        }`,
                                                        `inr_ndois_5y_${Date.now()}`,
                                                        popupSpecsForDes,
                                                    );
                                                }}
                                            />
                                        }
                                    />
                                    {!['production'].includes(
                                        process.env.NEXT_PUBLIC_ENVIRONMENT,
                                    ) && (
                                            <TooltipWithArrow
                                                tooltipText="Quick Price Entry"
                                                childNode={
                                                    <Image
                                                        src="/images/icons/plus-circle.svg"
                                                        id="quick_price_icon"
                                                        width={25}
                                                        height={25}
                                                        alt="quick_price_icon"
                                                        className="cursor-pointer"
                                                        onClick={() => window.open(
                                                            '/quick-price-entry',
                                                            'trade',
                                                            popupSpecsForQuickPrice,
                                                        )
                                                        }
                                                    />
                                                }
                                            />
                                        )}

                                    {<TooltipWithArrow
                                        tooltipText="FXO Input Form"
                                        childNode={<Image src="/images/icons/FXO.svg"
                                            id='deon-fxo'
                                            width={26}
                                            height={26}
                                            alt='FXO'
                                            className='cursor-pointer'
                                            onClick={() => { socketEmit('/fx-options/currency-pairs', { currentCurrencyTicker: '', dataLen: 1, isFxoButton: true }); }} />}
                                    />}
                                    <TooltipWithArrow
                                        tooltipText="IRS Input Form"
                                        childNode={<Image src="/images/icons/IRS.svg"
                                            id='deon-irs-form'
                                            width={26}
                                            height={26}
                                            alt='IRS'
                                            className='cursor-pointer relative'
                                            onClick={() => {
                                                socketEmit('/irs/user-irs-activity', { currentCurrencyTicker: '', dataLen: 1, isIrsButton: true, brokerType: irsBrokerType });
                                            }} />}
                                    />
                                    {getAuth()?.role === 'SuperAdmin' && <TooltipWithArrow
                                        tooltipText="Manage Sub Funds"
                                        childNode={<Image src="/images/icons/subfunds.svg"
                                            id='deon-subfunds'
                                            width={26}
                                            height={26}
                                            alt='Sub Funds'
                                            className='cursor-pointer relative'
                                            onClick={() => {
                                                window.open(
                                                    `${process.env.NEXT_PUBLIC_UI_URL}sub-funds`,
                                                    'sub_funds',
                                                    popupSpecsForDes
                                                );
                                            }} />}
                                    />}
                                    <TooltipWithArrow
                                        tooltipText="ATLAS"
                                        className="!absolute bottom-40 border-t border-gray-500 w-[80%] text-center pt-2"
                                        childNode={<Image src="/images/icons/atlas.svg"
                                            id='atlas-icon'
                                            width={24}
                                            height={26}
                                            alt='atlas'
                                            className='cursor-pointer relative'
                                            onClick={() => openTrackedWindow(PAGE_KEYS.ATLAS, '/atlas', popupSpecsForQuickPrice)}
                                        />
                                        }
                                    />
                                    <TooltipWithArrow
                                        tooltipText="Watchlist"
                                        childNode={<Image src="/images/icons/Watchlist.svg"
                                            id='deon-watchlist'
                                            width={26}
                                            height={26}
                                            alt='Watchlist'
                                            className='cursor-pointer relative'
                                            onClick={() => {
                                                openTrackedWindow(PAGE_KEYS.WATCHLIST, '/watchlist', popupSpecsForDes);
                                            }} />}
                                    />
                                    <TooltipWithArrow
                                        tooltipText="Child Windows"
                                        childNode={<Image src="/images/icons/cards.svg"
                                            id='child-windows'
                                            width={26}
                                            height={26}
                                            alt='Child Windows'
                                            className='cursor-pointer relative'
                                            onClick={() => {
                                                setIsChildWindowsOpen((prevState) => !prevState);
                                            }} />}
                                    />
                                    <HelpButton />
                                </div>)}
                            <ChildWindowsSidebar isOpen={isChildWindowsOpen} onClose={() => setIsChildWindowsOpen(false)} />
                            <div className='flex items-center gap-2 text-right'>

                                <div className='w-12 lg:w-24'>
                                    <Text className='text-gray-100 truncate'
                                        fontWeight='font-semibold' variant='bodySmall' title={`${user?.name || ''}`}>{user?.name || ''}</Text>
                                    <Text fontWeight='font-semibold' variant='caption'
                                        className=' text-gray-200 truncate' title={`${user?.companyName || ''}`}>{user?.companyName || ''}</Text>
                                </div>

                                <ActionList options={[{
                                    id: '1',
                                    onClick: () => {
                                        window.open('/profile', nanoid(5), popupSpecsForDes);
                                    },
                                    icon: '',
                                    label: 'My Profile'
                                }, { id: '2', onClick: () => { router.push('/change-password'); }, icon: '', label: 'Change Password' }, (getAuth()?.role === 'SuperAdmin' ? {
                                    id: '3',
                                    onClick: () => { window.open('/holidays', 'holiday', popupSpecsForDes); },
                                    icon: '',
                                    label: 'Holiday Calendar'
                                } : {}), ((getAuth()?.role) === 'Broker' ? {
                                    id: '4',
                                    onClick: () => { window.open('/download-orders', 'holiday', popupSpecsForDownloadOrders); },
                                    icon: '',
                                    label: 'Download Orders'
                                } : {}), ((getAuth()?.role) === 'Trader' && subFunds?.length ? {
                                    id: '5',
                                    onClick: () => setShowSubFundSidebar(true),
                                    icon: '',
                                    label: 'Manage Fund Allocation'
                                } : {}),
                                { id: '6', onClick: logoutFn, icon: '', label: 'Logout' }].filter((item) => item.id)} >

                                    {user?.profileUrl ? <Image
                                        src={getMediaFromCloudFrontUrl(user.profileUrl)}
                                        width={40}
                                        height={40}
                                        className="w-full h-full object-cover rounded-full select-none cursor-pointer"
                                        alt={user.profileUrl} />
                                        : <div
                                            className='text-basic-100 bg-primary-300 w-10 h-10 flex justify-center items-center rounded-full cursor-pointer'>{user?.name?.slice(0, 1).toUpperCase()}</div>}
                                </ActionList>
                            </div>
                        </div>
                    </div>
                </div>
                {
                    !complianceRoles.includes(getAuth()?.role)
                    && <MenuList menuItem={menuItem} className="text-gray-100  break-keep" listClass="xl:hidden justify-between max-w-[500px] mt-2 mx-auto " />
                }
                {showSubFundSidebar && <SubFundSidebar showSidebar={showSubFundSidebar} setShowSidebar={setShowSubFundSidebar} subFunds={subFunds} onFundsUpdated={handleFundsUpdated} />}
            </div>
        </>
    );
};

export default Header;
